import React, { useEffect, useState } from "react";
import {
  Card,
  Input,
  Button,
  Form,
  Row,
  Col,
  Upload,
  Image,
  Space,
  Typography,
  Breadcrumb,
  Divider,
} from "antd";
import {
  UploadOutlined,
  HomeOutlined,
  SaveOutlined,
  PlusOutlined,
  ShopOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { apiRequest } from "../../../service/apiRequest";
import { showSuccess, showError } from "../../../components/errorHandler";

const { Title, Text } = Typography;

const CompanySettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [logoFileList, setLogoFileList] = useState<UploadFile[]>([]);
  const [qrFileList, setQrFileList] = useState<UploadFile[]>([]);
  const [existingLogo, setExistingLogo] = useState<string>("");
  const [existingQr, setExistingQr] = useState<string>("");

  const resolveImageUrl = (path: string, type: "logo" | "qr" = "logo") => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:") || path.startsWith("blob:")) {
      return path;
    }
    const fileUrl = process.env.REACT_APP_FILE_URL || process.env.REACT_APP_API_URL || "";
    const cleanFileUrl = fileUrl ? (fileUrl.endsWith("/") ? fileUrl : `${fileUrl}/`) : "/";

    if (path.startsWith("/")) {
      path = path.slice(1);
    }

    if (path.includes("uploads/")) {
      return `${cleanFileUrl}${path}`;
    }
    return `${cleanFileUrl}uploads/organizations/logo/${path}`;
  };

  const fetchCompanyDetails = async () => {
    try {
      setFetching(true);
      const res = await apiRequest({
        method: "get",
        url: "/get-company-setting",
      });

      const details = Array.isArray(res)
        ? res[0]
        : Array.isArray(res?.data)
        ? res.data[0]
        : res?.data || res || {};

      const orgName = details.organization_name || details.company_name || details.od_name || "";
      const gstNo = details.od_gst_no || details.organization_gst || details.gst_no || details.gst_number || "";
      const mobile = details.organization_mobile || details.od_mobile || details.mobile || "";
      const email = details.organization_email || details.organisation_email || details.od_email || details.email || "";
      const address = details.organization_address || details.od_address || details.address || "";
      
      const logo =
        details.organization_logo_url ||
        details.organization_logo_link ||
        details.logo_link ||
        details.organization_logo ||
        details.od_logo ||
        details.logo ||
        "";

      const qr =
        details.od_qr_code_url ||
        details.od_qr_code_link ||
        details.qr_code_link ||
        details.od_qr_code ||
        details.qr_code ||
        "";

      form.setFieldsValue({
        organization_name: orgName,
        od_gst_no: gstNo,
        organization_mobile: mobile,
        organization_email: email,
        organization_address: address,
      });

      if (logo) {
        setExistingLogo(logo);
        setLogoFileList([
          {
            uid: "-1",
            name: "logo.png",
            status: "done",
            url: resolveImageUrl(logo, "logo"),
            thumbUrl: resolveImageUrl(logo, "logo"),
          },
        ]);
      } else {
        setLogoFileList([]);
      }

      if (qr) {
        setExistingQr(qr);
        setQrFileList([
          {
            uid: "-2",
            name: "qr_code.png",
            status: "done",
            url: resolveImageUrl(qr, "qr"),
            thumbUrl: resolveImageUrl(qr, "qr"),
          },
        ]);
      } else {
        setQrFileList([]);
      }
    } catch (err) {
      showError("Failed to fetch company details");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  const handleUpdate = async (values: any) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("od_gst_no", values.od_gst_no || "");
      formData.append("organization_name", values.organization_name || "");
      formData.append("organization_mobile", values.organization_mobile ? String(values.organization_mobile) : "");
      formData.append("organization_email", values.organization_email || "");
      formData.append("organisation_email", values.organization_email || "");
      formData.append("organization_address", values.organization_address || "");

      // Append logo if newly uploaded
      if (logoFileList.length > 0 && logoFileList[0].originFileObj) {
        formData.append("organization_logo", logoFileList[0].originFileObj);
        formData.append("attachments", logoFileList[0].originFileObj);
      }

      // Append QR code if newly uploaded
      if (qrFileList.length > 0 && qrFileList[0].originFileObj) {
        formData.append("od_qr_code", qrFileList[0].originFileObj);
        formData.append("qr_code", qrFileList[0].originFileObj);
      }

      await apiRequest({
        method: "put",
        url: "/update-company-setting",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      showSuccess("Company settings updated successfully!");
      fetchCompanyDetails();
    } catch (err) {
      showError("Failed to update company settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "0 16px 24px 16px", maxWidth: 960, margin: "0 auto" }}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>Settings</Breadcrumb.Item>
        <Breadcrumb.Item>Company Settings</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
        title={
          <Space align="center">
            <ShopOutlined style={{ fontSize: 20, color: "#1890ff" }} />
            <Title level={4} style={{ margin: 0 }}>
              Company & Organization Settings
            </Title>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
          disabled={fetching}
        >
          <Row gutter={20}>
            {/* Company Name */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Company Name"
                name="organization_name"
                rules={[{ required: true, message: "Please enter company name" }]}
              >
                <Input placeholder="e.g. Acme Corporation" maxLength={100} showCount />
              </Form.Item>
            </Col>

            {/* GST Number */}
            <Col xs={24} md={12}>
              <Form.Item
                label="GST Number (GSTIN)"
                name="od_gst_no"
                rules={[
                  {
                    pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                    message: "Please enter a valid 15-character GSTIN format",
                  },
                ]}
                getValueFromEvent={(e) => e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")}
              >
                <Input placeholder="e.g. 24AAAAA0000A1Z5" maxLength={15} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            {/* Mobile Number */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Mobile Number"
                name="organization_mobile"
                rules={[
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Please enter a valid 10-digit mobile number",
                  },
                ]}
                getValueFromEvent={(e) => e.target.value.replace(/\D/g, "").slice(0, 10)}
              >
                <Input placeholder="e.g. 9876543210" maxLength={10} showCount />
              </Form.Item>
            </Col>

            {/* Email Address */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Email Address"
                name="organization_email"
                rules={[{ type: "email", message: "Please enter a valid email address" }]}
              >
                <Input placeholder="e.g. info@company.com" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            {/* Company Address */}
            <Col span={24}>
              <Form.Item label="Company Address" name="organization_address">
                <Input.TextArea
                  placeholder="Enter full office / billing address"
                  rows={3}
                  maxLength={300}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0 24px 0" }} />

          <Row gutter={24}>
            {/* Organization Logo */}
            <Col xs={24} md={12}>
              <Form.Item label="Company Logo (organization_logo)">
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Upload
                    listType="picture-card"
                    fileList={logoFileList}
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={async ({ fileList }) => {
                      const updated = await Promise.all(
                        fileList.map(async (file) => {
                          if (file.originFileObj && !file.thumbUrl && !file.url) {
                            file.thumbUrl = await new Promise((res) => {
                              const r = new FileReader();
                              r.readAsDataURL(file.originFileObj as File);
                              r.onload = () => res(r.result as string);
                            });
                          }
                          return file;
                        })
                      );
                      setLogoFileList(updated);
                    }}
                    onRemove={() => {
                      setLogoFileList([]);
                      setExistingLogo("");
                    }}
                    accept="image/*"
                  >
                    {logoFileList.length === 0 && (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload Logo</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>

            {/* Payment QR Code */}
            <Col xs={24} md={12}>
              <Form.Item label="Payment QR Code (od_qr_code)">
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Upload
                    listType="picture-card"
                    fileList={qrFileList}
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={async ({ fileList }) => {
                      const updated = await Promise.all(
                        fileList.map(async (file) => {
                          if (file.originFileObj && !file.thumbUrl && !file.url) {
                            file.thumbUrl = await new Promise((res) => {
                              const r = new FileReader();
                              r.readAsDataURL(file.originFileObj as File);
                              r.onload = () => res(r.result as string);
                            });
                          }
                          return file;
                        })
                      );
                      setQrFileList(updated);
                    }}
                    onRemove={() => {
                      setQrFileList([]);
                      setExistingQr("");
                    }}
                    accept="image/*"
                  >
                    {qrFileList.length === 0 && (
                      <div>
                        <QrcodeOutlined style={{ fontSize: 20 }} />
                        <div style={{ marginTop: 8 }}>Upload QR Code</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0 24px 0" }} />

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              style={{ minWidth: 160, borderRadius: 8 }}
            >
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CompanySettings;